import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getProfessionals } from '../utils/storage.js';
import { recordLogin, recordHeartbeat, recordLogout, ENGAGEMENT_HEARTBEAT_INTERVAL_MS } from '../utils/engagementTracking.js';
import { isMasterUser } from '../utils/permissions.js';
import { limparTodosRascunhos } from '../utils/crmRascunhos.js';
import { CRM_SEM_RECARGA } from '../config/featureFlags.js';

const AuthContext = createContext(null);

// Auth server-side: cliente NÃO acessa fk_users.json (que agora é privado).
// Endpoints validam credenciais com hash PBKDF2 e retornam o user sanitizado.
// Normaliza payload de erro do servidor pra string. Endpoints podem retornar
// `error` como string ou como objeto (ex.: rate limit → {code, id, message}).
// Se renderizar objeto em JSX, dá React error #31 e trava a app inteira.
function errString(raw, fallback) {
  if (!raw) return fallback;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return raw.message || raw.code || fallback;
  return String(raw);
}

// Id ALEATÓRIO deste aparelho (128 bits, gerado uma vez, localStorage). Não é
// segredo de login: só faz o servidor reconhecer o aparelho, para o limite de
// tentativas nunca trancar o dono por causa de ataque de fora (16/10/2026).
function getDeviceId() {
  try {
    let id = localStorage.getItem('fk_device');
    if (id && /^[A-Za-z0-9_-]{16,128}$/.test(id)) return id;
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    id = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    localStorage.setItem('fk_device', id);
    return id;
  } catch {
    return undefined;   // sem localStorage/crypto: entra igual, só sem o reconhecimento
  }
}

async function loginRemote(email, password) {
  const r = await fetch('/api/auth-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, deviceId: getDeviceId() }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) return { error: errString(data?.error, 'Erro ao autenticar') };
  return { user: data.user, sessionToken: data.sessionToken, expiresAt: data.expiresAt };
}

async function changePasswordRemote(userId, currentPassword, newPassword, sessionToken) {
  const r = await fetch('/api/auth-change-password', {
    method: 'POST',
    // A sessão prova que é o dono da conta na troca obrigatória (sem senha atual).
    headers: { 'Content-Type': 'application/json', ...(sessionToken ? { 'x-session-token': sessionToken } : {}) },
    body: JSON.stringify({ userId, currentPassword, newPassword }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) return { error: errString(data?.error, 'Erro ao trocar senha') };
  return { ok: true };
}

// Best-effort logout: revoga sessão no DB. Falha silenciosamente — o cliente
// sempre limpa o sessionStorage independente do resultado.
async function logoutRemote(sessionToken, userId) {
  if (!sessionToken) return;
  try {
    await fetch('/api/auth-logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken, userId }),
      keepalive: true, // permite o request completar mesmo se o tab fechar
    });
  } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const heartbeatRef = useRef(null);

  // Verifica se a sessão expirou (server retorna expiresAt no login)
  const isExpired = (session) => {
    if (!session?.expiresAt) return false; // sessão legada sem expiração → válida
    return Date.now() > new Date(session.expiresAt).getTime();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = sessionStorage.getItem('fk_session');
      if (!stored) { setLoading(false); return; }
      let parsed;
      try { parsed = JSON.parse(stored); }
      catch {
        sessionStorage.removeItem('fk_session');
        setLoading(false);
        return;
      }
      // Sessão expirada local → logout silencioso
      if (isExpired(parsed)) {
        sessionStorage.removeItem('fk_session');
        setLoading(false);
        return;
      }
      // Sessão legada SEM token: desde 02/10/2026 os dados só vêm pelo cofre
      // autenticado (/api/data) — sem token a pessoa veria tudo vazio/velho.
      // Pede login de novo.
      if (!parsed.sessionToken) {
        sessionStorage.removeItem('fk_session');
        setLoading(false);
        return;
      }
      // Erros de rede/500 NÃO causam logout — só valid=false explícito.
      if (parsed.sessionToken) {
        try {
          const r = await fetch('/api/auth-session-validate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionToken: parsed.sessionToken }),
          });
          if (r.status === 200) {
            const data = await r.json();
            if (data?.valid === false) {
              sessionStorage.removeItem('fk_session');
              if (!cancelled) setLoading(false);
              return;
            }
          }
        } catch { /* rede caiu → mantém sessão (UX defensiva) */ }
      }
      if (cancelled) return;
      // Auto-promote Gerente
      if (parsed.professionalId) {
        try {
          const pros = getProfessionals();
          const myProf = pros.find(p => p.id === parsed.professionalId);
          if (myProf?.role === 'Gerente' && parsed.role !== 'admin') {
            parsed.role = 'admin';
            sessionStorage.setItem('fk_session', JSON.stringify(parsed));
          }
        } catch {}
      }
      setUser(parsed);
      if (parsed.mustChangePassword) setMustChangePassword(true);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Guarda de sessão morta ────────────────────────────────────────────────
  // INCIDENTE 10/07: sessão expirada no SERVIDOR com o app aberto (PWA
  // retomado não passa pelo boot) fazia todo o /api/db responder 401 e as
  // telas (Financeiro, contas etc.) renderizarem VAZIAS em silêncio — parecia
  // que os dados tinham sumido. Três defesas:
  //   1. check local de expiresAt a cada 5min (já existia);
  //   2. ao VOLTAR pro app (visibilitychange), revalida no servidor;
  //   3. sentinela global: QUALQUER 401 vindo do proxy /api/db força logout
  //      imediato com aviso — nunca mais tela vazia silenciosa.
  useEffect(() => {
    if (!user) return;
    // Válvula SÓ de dev (preview local com sessão fake): o Vite remove este
    // branch do bundle de produção — em prod a guarda é incondicional.
    if (import.meta.env.DEV && localStorage.getItem('fkDevNoAuthGuard') === '1') return;
    let dead = false;
    const forceExpire = (msg) => {
      if (dead) return;
      dead = true;
      try { sessionStorage.removeItem('fk_session'); } catch {}
      setUser(null);
      setMustChangePassword(false);
      alert(msg || 'Sua sessão expirou. Faça login de novo pra continuar vendo os dados.');
      window.location.reload();
    };

    const localCheck = () => {
      try {
        const stored = sessionStorage.getItem('fk_session');
        if (!stored) return;
        if (isExpired(JSON.parse(stored))) forceExpire();
      } catch {}
    };
    const interval = setInterval(localCheck, 5 * 60 * 1000);

    // Revalidação no servidor. SÓ desloga com 200 {valid:false} explícito:
    // 401 espúrio e 5xx/erro de rede por blip de Storage NUNCA derrubam sessão
    // válida. Throttles SEPARADOS (10min cada): a sentinela do /api/avec não
    // consome o budget do visibilitychange — voltar pra aba sempre tem direito
    // à própria revalidação, mesmo se um 401 espúrio revalidou minutos antes.
    let lastVisibleCheck = Date.now();
    let lastSentinelCheck = Date.now();
    // 200 {valid:false} → false; 200 válido → true; 5xx/erro → null (não decide)
    const checkOnce = async (sessionToken) => {
      const r = await fetch('/api/auth-session-validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken }),
      });
      if (r.status !== 200) return null;
      const data = await r.json().catch(() => null);
      return data?.valid === false ? false : true;
    };
    const revalidateSession = async (fonte) => {
      if (dead) return;
      const agora = Date.now();
      if (fonte === 'sentinela') {
        if (agora - lastSentinelCheck < 10 * 60 * 1000) return;
        lastSentinelCheck = agora;
      } else {
        if (agora - lastVisibleCheck < 10 * 60 * 1000) return;
        lastVisibleCheck = agora;
      }
      try {
        const parsed = JSON.parse(sessionStorage.getItem('fk_session') || '{}');
        if (!parsed.sessionToken) return;
        if (await checkOnce(parsed.sessionToken) !== false) return;
        // {valid:false} pode ser um blip que atravessou o servidor: espera ~2s
        // e re-checa UMA vez antes de deslogar — blip momentâneo não derruba
        // ninguém; sessão realmente revogada cai na segunda checagem.
        await new Promise(r => setTimeout(r, 2000));
        if (dead) return;
        if (await checkOnce(parsed.sessionToken) === false) forceExpire();
      } catch { /* rede caiu → mantém sessão */ }
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      localCheck();
      revalidateSession('visible');
    };
    document.addEventListener('visibilitychange', onVisible);

    // Sentinela: observa (sem alterar) as respostas dos proxies autenticados
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const resp = await origFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        if (resp.status === 401 && url.startsWith('/api/db')) forceExpire();
        else if (resp.status === 401 && url.startsWith('/api/avec')) {
          // 401 em relatório PII. Sessão legada SEM token não opera mais nada
          // de PII desde o endurecimento do proxy (02/09) — mantê-la logada só
          // produz tela zerada. COM token pode ser blip: revalida com throttle
          // e a decisão fica com o {valid:false} explícito do servidor.
          let hasToken = false;
          try { hasToken = !!JSON.parse(sessionStorage.getItem('fk_session') || '{}')?.sessionToken; } catch {}
          if (!hasToken) forceExpire('Seu acesso precisa ser renovado. Faça login de novo pra voltar a ver os dados.');
          else revalidateSession('sentinela');
        }
      } catch {}
      return resp;
    };

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.fetch = origFetch;
    };
  }, [user]);

  // Heartbeat enquanto há sessão ativa e a aba está visível.
  useEffect(() => {
    if (!user) return;
    const beat = () => {
      if (document.visibilityState === 'visible') recordHeartbeat(user.id);
    };
    heartbeatRef.current = setInterval(beat, ENGAGEMENT_HEARTBEAT_INTERVAL_MS);
    const onUnload = () => recordLogout(user.id);
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('visibilitychange', beat);
    return () => {
      clearInterval(heartbeatRef.current);
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('visibilitychange', beat);
    };
  }, [user]);

  const login = async (email, password) => {
    const result = await loginRemote(email, password);
    if (result.error || !result.user) {
      return { success: false, error: result.error || 'Email ou senha inválidos.' };
    }
    const found = result.user;
    const needsChange = !!found.mustChangePassword;
    // Auto-promote Gerente
    let effectiveRole = found.role;
    if (found.professionalId) {
      const pros = getProfessionals();
      const myProf = pros.find(p => p.id === found.professionalId);
      if (myProf?.role === 'Gerente') effectiveRole = 'admin';
    }
    const session = {
      id: found.id, name: found.name, email: found.email,
      role: effectiveRole, professionalId: found.professionalId,
      mustChangePassword: needsChange,
      sessionToken: result.sessionToken,
      expiresAt: result.expiresAt,
    };
    setUser(session);
    setMustChangePassword(needsChange);
    sessionStorage.setItem('fk_session', JSON.stringify(session));
    // Avisa o DataContext que já há sessão — ele re-busca dados que passam pelo
    // proxy autenticado (ex.: fotos via tabela professionals) e que podem ter
    // sido tentados no boot antes do login existir.
    try { window.dispatchEvent(new Event('fk-login')); } catch { /* SSR/no-window */ }
    recordLogin({ userId: found.id, role: effectiveRole, name: found.name });
    return { success: true, user: session, mustChangePassword: needsChange };
  };

  const changePassword = async (newPassword, currentPassword = null) => {
    if (!user) return { ok: false, error: 'Sem sessão' };
    const result = await changePasswordRemote(user.id, currentPassword, newPassword, user.sessionToken);
    if (result.ok) {
      const updated = { ...user, mustChangePassword: false };
      setUser(updated);
      setMustChangePassword(false);
      sessionStorage.setItem('fk_session', JSON.stringify(updated));
      return { ok: true };
    }
    return { ok: false, error: result.error || 'Erro ao trocar senha' };
  };

  const logout = () => {
    if (user) {
      recordLogout(user.id);
      // Revoga sessão server-side (fire-and-forget — não bloqueia logout local)
      logoutRemote(user.sessionToken, user.id);
    }
    setUser(null);
    setMustChangePassword(false);
    sessionStorage.removeItem('fk_session');
    // 27/10/2026 (CRM_SEM_RECARGA): o rascunho do CRM não passa para quem entrar depois na mesma aba
    if (CRM_SEM_RECARGA) { try { limparTodosRascunhos(); } catch { /* ok */ } }
  };

  const isMaster = isMasterUser(user);
  return <AuthContext.Provider value={{ user, loading, login, logout, mustChangePassword, changePassword, isMaster }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
